import type { MaintenanceTask } from '@/lib/db/schema'
import type { SubscriptionTier } from '@/lib/subscription-config'
import { UTILITIES_ENABLED } from '@/lib/features'

/**
 * What counts toward a plan's entity limit (`maxEntities`), defined once.
 *
 * The limit measures capacity, not history. Counted: items, parts, utilities
 * (only while UTILITIES_ENABLED; hidden utilities don't count, see drizzle/0034),
 * and maintenance tasks that are not completed. Not counted: completed tasks,
 * utility bills, and activity logs. Otherwise a free user would fill the quota
 * just by marking work done and logging bills month after month.
 *
 * Enforcement reads the denormalized `subscriptions.totalEntitiesUsed` counter,
 * so every place a task's status or existence changes must move that counter
 * by `countedTaskDelta`. The SQL recount (`getEntityCounts` in
 * server/entity-limits.ts) and the backfill migration
 * (drizzle/0032_count_active_entities.sql) use the same status list.
 *
 * Client-safe: no db import.
 */

export type TaskStatus = NonNullable<MaintenanceTask['status']>

/**
 * Whether a task in each status takes up capacity. A Record over the enum, so
 * adding a status is a compile error until someone decides whether it counts.
 */
const TASK_STATUS_COUNTED: Record<TaskStatus, boolean> = {
  pending: true,
  in_progress: true,
  overdue: true,
  completed: false,
}

/** The counted statuses, for SQL `IN (...)`. NULL status is counted too. */
export const COUNTED_TASK_STATUSES = (Object.keys(TASK_STATUS_COUNTED) as TaskStatus[]).filter(
  (status) => TASK_STATUS_COUNTED[status]
)

/**
 * Whether a task with this status counts toward the limit. `status` has no NOT
 * NULL constraint and a NULL row is an open task, so null/undefined counts
 * (the column defaults to 'pending'). Mirrors `status IS NULL OR status IN
 * (COUNTED_TASK_STATUSES)`, never `status <> 'completed'`, which drops NULLs.
 */
export function isCountedTaskStatus(status: TaskStatus | null | undefined): boolean {
  return status == null || TASK_STATUS_COUNTED[status]
}

/** Counter change when a task moves from one status to another: -1, 0, or +1. */
export function countedTaskDelta(
  before: TaskStatus | null | undefined,
  after: TaskStatus | null | undefined
): number {
  return Number(isCountedTaskStatus(after)) - Number(isCountedTaskStatus(before))
}

/** How many of these new tasks count toward the limit. */
export function countCountedTasks(tasks: ReadonlyArray<{ status?: TaskStatus | null }>): number {
  return tasks.filter((task) => isCountedTaskStatus(task.status)).length
}

/**
 * What happened to a completed recurring task's next occurrence. Returned by
 * the completion path (server/task-completion.ts) and on the PUT response as
 * `recurrence`, where the app words it (toastRecurrenceSkipped).
 */
export type Recurrence = {
  scheduled: boolean
  dueDate?: string
  /** Why the next occurrence wasn't created. */
  skipped?: 'plan_limit' | 'error'
  /** Usage when skipped for the plan limit, for the upgrade prompt. */
  usage?: { current: number; limit: number; tier: SubscriptionTier }
}

/** User-facing name for what the limit counts. */
export const COUNTED_ENTITIES_LABEL = UTILITIES_ENABLED
  ? 'active items, parts, tasks, and utilities'
  : 'active items, parts, and tasks'
